// Выбор снимка с диска и показ его ДО отправки.
//
// Показ до отправки — не украшение. Это единственное место, где человек может убедиться, что
// приложил тот снимок, а не соседний: после отправки файл уедет на сервер, и вернуть его
// оттуда нельзя.
//
// Общий с 26.08.2026: был выбором ЧЕКА в разделе «Деньги», и понадобился второй раз — для
// подтверждения перевода при отметке выплаты (сценарий 76). Второй такой же компонент рядом
// означал бы второй предпросмотр, второй список допустимых форматов и вторую утечку адреса
// объекта в памяти.
//
// Предпросмотр строится из локального файла (`URL.createObjectURL`), сеть для него не нужна:
// файл уже лежит на диске у человека. Это единственный оптимизм во всём разделе, и он законен
// — мы показываем то, чем владеем локально, а не то, что якобы сделал сервер.

import { useEffect, useState } from 'react';

import { RiImageAddLine } from '@remixicon/react';

import * as Button from '@/shared/ui/button';
import * as Hint from '@/shared/ui/hint';
import * as Label from '@/shared/ui/label';

export interface FilePickerProps {
  /** Опознаватель поля: подпись связывается с ним, и на экране их бывает два. */
  id: string;
  /** Что именно прикладывают: «Снимок чека», «Подтверждение перевода». */
  label: string;
  /** Приглашение внутри рамки. Пусто — собирается из подписи. */
  подсказка?: string;
  file: File | null;
  onPick: (file: File | null) => void;
  disabled?: boolean;
}

export function FilePicker({
  id,
  label,
  подсказка,
  file,
  onPick,
  disabled = false,
}: FilePickerProps) {
  const [превью, setПревью] = useState<string | null>(null);

  useEffect(() => {
    if (file === null) {
      setПревью(null);
      return;
    }
    const адрес = URL.createObjectURL(file);
    setПревью(адрес);
    // Отзываем адрес при смене файла: иначе браузер держит объект в памяти до перезагрузки
    // страницы, а на КПП вкладку не перезагружают сутками.
    return () => URL.revokeObjectURL(адрес);
  }, [file]);

  return (
    <div className='flex flex-col gap-2'>
      <Label.Root htmlFor={id}>{label}</Label.Root>

      {превью !== null && file !== null ? (
        <div className='flex flex-col gap-2'>
          <img
            src={превью}
            alt={label}
            className='max-h-60 w-full rounded-lg border border-stroke-soft-200 object-contain'
          />
          <div className='flex items-center justify-between gap-2'>
            <span className='truncate text-paragraph-xs text-text-sub-600'>
              {file.name} · {(file.size / 1024).toFixed(0).replace('.', ',')} КБ
            </span>
            <Button.Root
              type='button'
              variant='neutral'
              mode='ghost'
              size='xxsmall'
              onClick={() => onPick(null)}
              disabled={disabled}
            >
              Убрать
            </Button.Root>
          </div>
        </div>
      ) : (
        <label className='flex cursor-pointer flex-col items-center gap-2 rounded-lg border border-dashed border-stroke-soft-200 p-5 text-center'>
          <RiImageAddLine className='size-6 text-text-soft-400' />
          <span className='text-paragraph-sm text-text-sub-600'>
            {подсказка ?? `Выбрать с диска: ${label.toLowerCase()}`}
          </span>
          <input
            id={id}
            type='file'
            accept='image/jpeg,image/png,image/webp,application/pdf'
            className='hidden'
            disabled={disabled}
            onChange={(событие) => onPick(событие.target.files?.[0] ?? null)}
          />
        </label>
      )}

      <Hint.Root>
        <Hint.Icon />
        Принимаются jpeg, png, webp и pdf. Снимки с айфона в формате HEIC не принимаются: сохраните
        как jpeg.
      </Hint.Root>
    </div>
  );
}
